import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, CheckCircle2, AlertTriangle, RotateCcw, Info } from 'lucide-react';

type FieldDiff = { field: string; label: string; currentValue: any; newValue: any };

type PreviewOrder = {
  externalKey: string;
  orderNumber: string;
  productName: string | null;
  customerName: string | null;
  localStatus: string | null;
  action: 'create' | 'update' | 'skip';
  reason: 'already_authorized_or_beyond' | 'manually_modified' | null;
  message: string | null;
  fieldDiffs: FieldDiff[];
  error: string | null;
};

type SkippedOrder = { orderNumber: string; externalKey: string | null; status: string; reason: string };

type ApplyResult = {
  summary: { total?: number; created: number; updated: number; skipped: number; errors?: number };
  skippedOrders?: SkippedOrder[];
  errors?: Array<{ error: string }>;
};

type PageState = 'input' | 'preview' | 'result';

const STATUS_LABELS: Record<string, string> = {
  CREATED: 'Creada',
  AUTHORIZED: 'Autorizada',
  COMPLETED: 'Completada',
  CLOSED: 'Cerrada',
};

const SKIP_TEXT: Record<string, string> = {
  already_authorized_or_beyond:
    'Ya fue autorizada (o está completada/cerrada): no se actualiza para no alterar producción en curso.',
  manually_modified:
    'Tiene cambios manuales hechos en el sistema: no se actualiza para no sobrescribirlos. Si desea reemplazarlos, use "Actualizar orden desde Comercia" dentro de la propia orden.',
};

function todayMinus(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

const fmt = (v: any) => (v === null || v === undefined || v === '' ? '—' : String(v));

export default function ComerciaOrderSync() {
  const [pageState, setPageState] = useState<PageState>('input');
  const [numbers, setNumbers] = useState('');
  const [fecha, setFecha] = useState(todayMinus(7));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [orders, setOrders] = useState<PreviewOrder[]>([]);
  const [notFound, setNotFound] = useState<string[]>([]);
  const [totalFromComercia, setTotalFromComercia] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<ApplyResult | null>(null);

  const token = () => localStorage.getItem('token');
  const headers = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` });

  const handleSearch = async () => {
    setError(null);
    if (numbers.trim().length === 0) {
      setError('Ingresa al menos un número de orden.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/production-orders/comercia-sync/preview', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ orderNumbers: numbers, fecha }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error([data.error, data.details].filter(Boolean).join(': ') || 'Error al consultar Comercia');
      setOrders(data.orders);
      setNotFound(data.notFound);
      setTotalFromComercia(data.totalFromComercia);
      // Pre-select everything that can actually be written.
      setSelected(new Set<string>(data.orders.filter((o: PreviewOrder) => o.action !== 'skip' && !o.error).map((o: PreviewOrder) => o.externalKey)));
      setExpanded(new Set());
      setPageState('preview');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await fetch('/api/production-orders/import-from-external', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ fecha, externalKeys: Array.from(selected) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error([data.error, data.details].filter(Boolean).join(': ') || 'Error al actualizar las órdenes');
      setResult(data);
      setPageState('result');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setPageState('input');
    setOrders([]);
    setNotFound([]);
    setSelected(new Set());
    setExpanded(new Set());
    setResult(null);
    setError(null);
  };

  const toggle = (set: Set<string>, key: string) => {
    const next = new Set(set);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  };

  const selectable = orders.filter((o) => o.action !== 'skip' && !o.error);
  const skipped = orders.filter((o) => o.action === 'skip');
  const counts = {
    create: orders.filter((o) => o.action === 'create').length,
    update: orders.filter((o) => o.action === 'update').length,
    skip: skipped.length,
  };

  const actionBadge = (o: PreviewOrder) => {
    if (o.error) return <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Error</span>;
    if (o.action === 'create') return <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Se creará</span>;
    if (o.action === 'update') return <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Se actualizará</span>;
    return <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">No se actualizará</span>;
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <RefreshCw className="h-6 w-6 text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-900">Sincronizar órdenes con Comercia</h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-3 mb-4 text-sm text-red-700">{error}</div>
      )}

      {pageState === 'input' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-2 text-sm text-blue-800">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <p>
              Se consultará Comercia desde la fecha indicada y solo se revisarán las órdenes que escribas.
              Primero verás un resumen; nada se guarda hasta que confirmes.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Números de orden — separados por coma (también se aceptan saltos de línea)
            </label>
            <textarea
              className="w-full h-32 p-3 border border-gray-300 rounded-md font-mono text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="12345, 12346, 12350"
              value={numbers}
              onChange={(e) => setNumbers(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha aproximada de creación o actualización en Comercia
            </label>
            <input
              type="date"
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-1">Usa una fecha igual o anterior a la real; Comercia devuelve las órdenes desde ese día.</p>
          </div>
          <Button
            onClick={handleSearch}
            disabled={loading || numbers.trim().length === 0 || !fecha}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {loading ? 'Consultando Comercia…' : 'Buscar en Comercia'}
          </Button>
        </div>
      )}

      {pageState === 'preview' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Comercia devolvió {totalFromComercia} órdenes desde {fecha}; {orders.length} coinciden con tu lista:
            {' '}<strong>{counts.create}</strong> nuevas, <strong>{counts.update}</strong> a actualizar,{' '}
            <strong>{counts.skip}</strong> que no se actualizarán.
          </p>

          {notFound.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-red-700 mb-1">No encontradas en Comercia desde {fecha} ({notFound.length})</p>
              <p className="text-xs text-red-600 mb-2">Verifica el número o usa una fecha anterior.</p>
              <div className="flex flex-wrap gap-2">
                {notFound.map((n) => (
                  <span key={n} className="text-xs bg-red-100 text-red-700 border border-red-300 px-2 py-0.5 rounded-full">{n}</span>
                ))}
              </div>
            </div>
          )}

          {orders.length > 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-2 w-10">
                        <input
                          type="checkbox"
                          aria-label="Seleccionar todas"
                          disabled={selectable.length === 0}
                          checked={selectable.length > 0 && selected.size === selectable.length}
                          onChange={(e) => setSelected(e.target.checked ? new Set(selectable.map((o) => o.externalKey)) : new Set())}
                        />
                      </th>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Nº Orden</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Producto</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Cliente</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Estatus local</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Resultado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {orders.map((o) => {
                      const canSelect = o.action !== 'skip' && !o.error;
                      const hasDetail = o.action === 'skip' || !!o.error || o.fieldDiffs.length > 0;
                      return (
                        <>
                          <tr key={o.externalKey} className={o.action === 'skip' ? 'bg-amber-50/40' : 'hover:bg-gray-50'}>
                            <td className="px-4 py-2">
                              <input
                                type="checkbox"
                                disabled={!canSelect}
                                checked={selected.has(o.externalKey)}
                                onChange={() => setSelected((s) => toggle(s, o.externalKey))}
                              />
                            </td>
                            <td className="px-4 py-2 font-mono font-medium text-gray-900">{o.orderNumber}</td>
                            <td className="px-4 py-2 text-gray-700">{fmt(o.productName)}</td>
                            <td className="px-4 py-2 text-gray-500">{fmt(o.customerName)}</td>
                            <td className="px-4 py-2">
                              {o.localStatus ? (
                                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                                  {STATUS_LABELS[o.localStatus] ?? o.localStatus}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                {actionBadge(o)}
                                {hasDetail && (
                                  <button className="text-xs text-blue-600 hover:underline" onClick={() => setExpanded((s) => toggle(s, o.externalKey))}>
                                    {expanded.has(o.externalKey) ? 'Ocultar' : o.action === 'update' ? 'Ver cambios' : 'Ver motivo'}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {expanded.has(o.externalKey) && (
                            <tr key={`${o.externalKey}-d`} className="bg-gray-50">
                              <td />
                              <td colSpan={5} className="px-4 py-3 text-sm">
                                {o.error && <p className="text-red-700">{o.error}</p>}
                                {o.action === 'skip' && (
                                  <p className="text-amber-800 flex gap-2">
                                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                    {(o.reason && SKIP_TEXT[o.reason]) || o.message}
                                  </p>
                                )}
                                {o.action === 'update' && !o.error && (
                                  <table className="text-xs">
                                    <thead>
                                      <tr className="text-gray-500">
                                        <th className="text-left pr-6 py-1">Campo</th>
                                        <th className="text-left pr-6 py-1">Actual</th>
                                        <th className="text-left py-1">En Comercia</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {o.fieldDiffs.map((d) => (
                                        <tr key={d.field}>
                                          <td className="pr-6 py-0.5 text-gray-700">{d.label}</td>
                                          <td className="pr-6 py-0.5 text-gray-500">{fmt(d.currentValue)}</td>
                                          <td className="py-0.5 text-gray-900 font-medium">{fmt(d.newValue)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-gray-500">
              Ninguna de las órdenes ingresadas apareció en Comercia desde esa fecha.
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={reset} className="flex items-center gap-2">
              <RotateCcw className="h-4 w-4" />
              Nueva búsqueda
            </Button>
            <Button
              onClick={handleApply}
              disabled={loading || selected.size === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              {loading ? 'Procesando…' : `Crear / actualizar seleccionadas (${selected.size})`}
            </Button>
          </div>
        </div>
      )}

      {pageState === 'result' && result && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-300 rounded-lg p-5 flex items-center gap-4">
            <CheckCircle2 className="h-8 w-8 text-green-600 flex-shrink-0" />
            <div>
              <p className="text-lg font-semibold text-green-800">
                {result.summary.created} creada(s), {result.summary.updated} actualizada(s)
                {result.summary.skipped > 0 ? `, ${result.summary.skipped} omitida(s)` : ''}
              </p>
            </div>
          </div>

          {(result.skippedOrders?.length ?? 0) > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 space-y-2">
              <p className="text-sm font-semibold text-amber-800">Órdenes que no se actualizaron</p>
              {result.skippedOrders!.map((s) => (
                <p key={s.externalKey ?? s.orderNumber} className="text-sm text-amber-800">
                  <span className="font-mono font-medium">{s.orderNumber}</span>: {SKIP_TEXT[s.reason] ?? s.reason}
                </p>
              ))}
            </div>
          )}

          {(result.errors?.length ?? 0) > 0 && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-4 text-sm text-red-700">
              {result.errors!.length} orden(es) tuvieron error al procesarse. Revisa la bitácora de auditoría.
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={reset} variant="outline" className="flex items-center gap-2">
              <RotateCcw className="h-4 w-4" />
              Sincronizar más órdenes
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
